import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  setPlaylist,
  SetPlaylistArgs,
} from "../../store/actions/abPlayerStore";
import { getCurrentTrackFromPlaylist } from "../../store/selectors/abPlayerSelectors";
import { useAtomValue } from "jotai";
import { currentTimeAtom, mediaElementAtom } from "../../atoms/audioAtom";
import { getFooterWaveformRef } from "./waveformHooks";
import { default as WaveSurferRef } from "wavesurfer.js";

export const useGetFooterPlayerRef = () => {
  const footerPlayerRef = useAtomValue(mediaElementAtom);
  return footerPlayerRef;
};

// This gets the visual waveform on the footer. It should not be used for audio playback.
export const useGetFooterWaveformRef = () => {
  const { isFooterReady } = useAppSelector((store) => store.abPlayerStore);
  const footerPlayerRef = useRef<WaveSurferRef | null>(null);
  useEffect(() => {
    if (!isFooterReady) return;
    footerPlayerRef.current = getFooterWaveformRef();
  }, [isFooterReady]);

  return footerPlayerRef;
};

export const useCurrentProgressForPlaylistId = (currentPlaylistId: number) => {
  const { isFooterReady, playlistId } = useAppSelector(
    (store) => store.abPlayerStore,
  );
  const currentPosition = useAtomValue(currentTimeAtom);
  const footerPlayerRef = useGetFooterPlayerRef();

  return useMemo(() => {
    if (!isFooterReady) {
      return 0;
    }
    if (!footerPlayerRef) {
      return 0;
    }
    if (currentPlaylistId !== playlistId) {
      return 0;
    }
    return (currentPosition / footerPlayerRef.duration) * 100;
  }, [
    isFooterReady,
    footerPlayerRef,
    currentPlaylistId,
    playlistId,
    currentPosition,
  ]);
};

export const usePlayPauseOnFooter = () => {
  const footerPlayerRef = useGetFooterPlayerRef();

  const handleClick = () => {
    if (footerPlayerRef) {
      if (footerPlayerRef.paused) {
        void footerPlayerRef.play();
      } else {
        footerPlayerRef.pause();
      }
    }
  };
  return { handleClick };
};

export const useSetPlayList = () => {
  const dispatch = useAppDispatch();
  return useCallback(
    (args: SetPlaylistArgs) => {
      dispatch(setPlaylist(args));
    },
    [dispatch],
  );
};

export const useCurrentProgressForPlaylistTrackId = (trackId: number) => {
  const { isFooterReady } = useAppSelector((store) => store.abPlayerStore);

  const currentPosition = useAtomValue(currentTimeAtom);

  const currentTrack = useAppSelector(getCurrentTrackFromPlaylist());
  const footerPlayerRef = useGetFooterPlayerRef();

  return useMemo(() => {
    if (!isFooterReady) {
      return 0;
    }
    if (!footerPlayerRef) {
      return 0;
    }
    if (!currentTrack) {
      return 0;
    }
    if (trackId !== currentTrack.id) {
      return 0;
    }
    return (currentPosition / footerPlayerRef.duration) * 100;
  }, [isFooterReady, currentPosition, footerPlayerRef, trackId, currentTrack]);
};

export const useHandlePause = () => {
  const audioElement = useAtomValue(mediaElementAtom);
  const handlePause = () => {
    if (!audioElement) {
      return;
    }
    audioElement.pause();
  };
  return { handlePause };
};

export const useIsFooterPlayerPaused = () => {
  const footerPlayerRef = useGetFooterPlayerRef();
  if (!footerPlayerRef) {
    return true;
  }

  return footerPlayerRef.paused;
};
